// src/presentation/pages/ClinicalExamination/sections/PriorNeurologicalDiagnoses.tsx
import React from "react";
import { CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

interface PriorNeurologicalDiagnosesProps {
  value: boolean | null;
  condition: string;
  yearOfDiagnosis: string;
  onConditionChange: (value: string) => void;
  onYearChange: (value: string) => void;
  onChange: (value: boolean) => void;
}

const getConditionOptions = (t: TFunction<"patients">) => [
  { value: "Dementia", label: t("clinicalSections.priorNeurologicalDiagnoses.conditions.dementia") },
  { value: "Alzheimer's Disease", label: t("clinicalSections.priorNeurologicalDiagnoses.conditions.alzheimers") },
  { value: "Parkinson's Disease", label: t("clinicalSections.priorNeurologicalDiagnoses.conditions.parkinsons") },
  { value: "Multiple Sclerosis", label: t("clinicalSections.priorNeurologicalDiagnoses.conditions.multipleSclerosis") },
  { value: "Epilepsy", label: t("clinicalSections.priorNeurologicalDiagnoses.conditions.epilepsy") },
  { value: "Stroke-related condition", label: t("clinicalSections.priorNeurologicalDiagnoses.conditions.strokeRelated") },
  { value: "Traumatic Brain Injury", label: t("clinicalSections.priorNeurologicalDiagnoses.conditions.tbi") },
  { value: "Huntington's Disease", label: t("clinicalSections.priorNeurologicalDiagnoses.conditions.huntingtons") },
  { value: "ALS", label: t("clinicalSections.priorNeurologicalDiagnoses.conditions.als") },
  { value: "Other", label: t("clinicalSections.priorNeurologicalDiagnoses.conditions.other") },
];

export const PriorNeurologicalDiagnoses: React.FC<
  PriorNeurologicalDiagnosesProps
> = ({
  value,
  condition,
  yearOfDiagnosis,
  onChange,
  onConditionChange,
  onYearChange,
}) => {
  const { t } = useTranslation("patients");
  const showFields = value === true;
  const CONDITION_OPTIONS = getConditionOptions(t);

  return (
    <div className="border-gray-200">
      <CardHeader className="pb-3 !px-0">
        <CardTitle className="text-base font-semibold text-gray-900">
          {t("clinicalSections.priorNeurologicalDiagnoses.title")}
        </CardTitle>
        <p className="text-sm text-gray-500">
          {t("clinicalSections.priorNeurologicalDiagnoses.question")}
        </p>
      </CardHeader>
      <CardContent className="space-y-4 !px-0">
        <RadioGroup
          value={value?.toString()}
          onValueChange={(val) => onChange(val === "true")}
          className="flex gap-6"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="true" id="neuro-yes" />
            <Label
              htmlFor="neuro-yes"
              className="text-[#1B1D21] cursor-pointer text-sm font-normal"
            >
              {t("clinicalSections.yes")}
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="false" id="neuro-no" />
            <Label
              htmlFor="neuro-no"
              className="text-[#1B1D21] cursor-pointer text-sm font-normal"
            >
              {t("clinicalSections.no")}
            </Label>
          </div>
        </RadioGroup>

        {showFields && (
          <div className="flex flex-col gap-2">
            <div>
              <Label htmlFor="condition" className="text-[#5F646F]">
                {t("clinicalSections.priorNeurologicalDiagnoses.conditionLabel")}
              </Label>
              <Select value={condition} onValueChange={onConditionChange}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder={t("clinicalSections.priorNeurologicalDiagnoses.selectCondition")} />
                </SelectTrigger>
                <SelectContent
                  position="popper"
                  side="bottom"
                  align="start"
                  sideOffset={4}
                  className="z-50 cursor-pointer"
                >
                  {CONDITION_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="year" className="text-[#5F646F]">
                {t("clinicalSections.priorNeurologicalDiagnoses.yearLabel")}
              </Label>
              <Input
                id="year"
                type="text"
                inputMode="numeric"
                placeholder="YYYY"
                value={yearOfDiagnosis}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, "").slice(0, 4);
                  onYearChange(v);
                }}
                className="mt-1 w-40"
              />
            </div>
          </div>
        )}
      </CardContent>
    </div>
  );
};
